import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import type { SelectOption } from 'naive-ui';
import {
  PlaybookActionType,
  PlaybookEvaluationType,
  PlaybookRunStatus,
  type DocumentBlock,
  type ListPlaybookEvaluationsResponse,
  type PlaybookRunInDb,
  type PlaybookRunSearchResult,
} from '@/api';
import {
  countEvaluationProgress,
  mapFailedEvaluations,
  mapRunToClauseEntries,
  mapRunToExtractions,
  mapRunToFindings,
  mapRunToSuggestions,
  type PlaybookClauseEntry,
} from '@/office/playbooks/mapPlaybookRunResults';
import {
  applySuggestionToWord,
  focusSuggestionInWord,
  readCurrentWordDocument,
  type ApplySuggestionResult,
} from '@/office/word/playbookWord';
import { PlaybooksService } from '@/services/playbooks/playbooksService';
import type {
  UncommonsCheckFinding,
  WordPaneStatus,
  WordPromptError,
  WordSuggestion,
  WordSuggestionStatus,
} from '@/types/playbookWordAddin';
import { extractErrorDetail } from '@/utils/errorHandler';

/**
 * State for the Word add-in's Playbooks tab. Lives in a store (not the view) so
 * the selected playbook, the active run and the locally applied/declined
 * suggestions survive switching between the task pane's bottom tabs.
 *
 * The public surface deliberately mirrors the shape the task pane's panes
 * already render (suggestions, clause entries, findings, per-pane status and
 * progress labels) — see `mapPlaybookRunResults.ts` for the mapping.
 */

const PLAYBOOK_PAGE_SIZE = 50;
const RUN_PAGE_SIZE = 25;

const ACTIVE_RUN_STATUSES = new Set<PlaybookRunStatus>([
  PlaybookRunStatus.PENDING,
  PlaybookRunStatus.RUNNING,
  PlaybookRunStatus.SUMMARIZING,
]);

/**
 * The Word document as one block per page. The backend renders the blocks into
 * per-page texts anyway, so sending the whole page text as a single block keeps
 * the payload small instead of shipping one block per paragraph.
 */
function toPageBlocks(blocks: DocumentBlock[]): DocumentBlock[] {
  const pages = new Map<number, string[]>();
  for (const block of blocks) {
    const pageLines = pages.get(block.page_number);
    if (pageLines) {
      pageLines.push(block.text);
    } else {
      pages.set(block.page_number, [block.text]);
    }
  }

  return [...pages.entries()]
    .sort(([pageA], [pageB]) => pageA - pageB)
    .map(([pageNumber, pageLines]) => ({
      text: pageLines.join('\n'),
      page_number: pageNumber,
    }));
}

export const useOfficePlaybookRunStore = defineStore('officePlaybookRun', () => {
  const playbookOptions = ref<SelectOption[]>([]);
  const playbooksLoading = ref<boolean>(false);
  const playbooksError = ref<string | null>(null);
  const selectedPlaybookUid = ref<string | null>(null);
  // Remote search replaces the options wholesale; keep the current selection's
  // label around so the select never shows a bare uid.
  const selectedPlaybookOption = ref<SelectOption | null>(null);

  const evaluations = ref<ListPlaybookEvaluationsResponse>([]);

  const run = ref<PlaybookRunInDb | null>(null);
  const isStartingRun = ref<boolean>(false);
  const runError = ref<string | null>(null);

  // Applying and declining are local, per-session decisions — the backend has no
  // notion of an accepted suggestion. Keyed by the mapper's deterministic ids so
  // they survive a websocket-triggered refetch of the run.
  const suggestionStatuses = ref<Map<string, WordSuggestionStatus>>(new Map());
  const closedFindingIds = ref<Set<string>>(new Set());
  // Suggestions whose text could not be located in the document; run-scoped like
  // the statuses above, so the panes can flag them after an "apply all".
  const failedSuggestionIds = ref<Set<string>>(new Set());

  const applyingSuggestionId = ref<string | null>(null);
  const focusingSuggestionId = ref<string | null>(null);
  const focusingFindingId = ref<string | null>(null);

  const isRunActive = computed(
    () => run.value !== null && ACTIVE_RUN_STATUSES.has(run.value.status)
  );

  const runFailureMessage = computed(() => {
    if (!run.value) return null;
    if (run.value.status === PlaybookRunStatus.FAILED) return 'The playbook run failed.';
    if (run.value.status === PlaybookRunStatus.CANCELLED) return 'The playbook run was cancelled.';
    return null;
  });

  function withLocalStatus(suggestions: WordSuggestion[]): WordSuggestion[] {
    return suggestions.map(suggestion => {
      const status = suggestionStatuses.value.get(suggestion.id);
      return status ? { ...suggestion, status } : suggestion;
    });
  }

  const suggestions = computed<WordSuggestion[]>(() =>
    run.value ? withLocalStatus(mapRunToSuggestions(run.value, evaluations.value)) : []
  );

  const pendingSuggestions = computed(() =>
    suggestions.value.filter(
      suggestion => suggestion.result_type !== 'extraction' && suggestion.status === 'pending'
    )
  );

  // Extraction cards are informational, so they carry no local applied/declined state.
  const extractions = computed<WordSuggestion[]>(() =>
    run.value ? mapRunToExtractions(run.value, evaluations.value) : []
  );

  const clauseEntries = computed<PlaybookClauseEntry[]>(() =>
    run.value
      ? mapRunToClauseEntries(run.value, evaluations.value).map(entry => ({
          ...entry,
          suggestions: withLocalStatus(entry.suggestions),
        }))
      : []
  );

  const pendingClauseSuggestions = computed(() =>
    clauseEntries.value.flatMap(entry =>
      entry.suggestions.filter(suggestion => suggestion.status === 'pending')
    )
  );

  const uncommonsFindings = computed<UncommonsCheckFinding[]>(() =>
    run.value
      ? mapRunToFindings(run.value, evaluations.value).map(finding =>
          closedFindingIds.value.has(finding.id)
            ? { ...finding, status: 'closed' as const }
            : finding
        )
      : []
  );

  /**
   * Panes report `loading` for as long as the run is active — results render
   * alongside it as they arrive — and only resolve to success/empty once the run
   * reaches a terminal status. A failed run is reported once via
   * `runFailureMessage`, not per pane.
   */
  function paneStatus(hasContent: boolean): WordPaneStatus {
    if (!run.value) return 'idle';
    if (isRunActive.value) return 'loading';
    return hasContent ? 'success' : 'empty';
  }

  function progressLabel(type: PlaybookEvaluationType): string {
    if (!run.value || !isRunActive.value) return '';
    const { done, total } = countEvaluationProgress(run.value, evaluations.value, type);
    if (!total) return '';
    return `${done} / ${total} ${total === 1 ? 'evaluation' : 'evaluations'} completed`;
  }

  function failures(type: PlaybookEvaluationType): WordPromptError[] {
    return run.value ? mapFailedEvaluations(run.value, evaluations.value, type) : [];
  }

  const classificationStatus = computed(() => paneStatus(suggestions.value.length > 0));
  const extractionStatus = computed(() => paneStatus(extractions.value.length > 0));
  const clauseValidationStatus = computed(() => paneStatus(clauseEntries.value.length > 0));
  const uncommonsStatus = computed(() => paneStatus(uncommonsFindings.value.length > 0));

  const classificationProgressLabel = computed(() =>
    progressLabel(PlaybookEvaluationType.CLASSIFICATION)
  );
  const extractionProgressLabel = computed(() => progressLabel(PlaybookEvaluationType.EXTRACTION));
  const clauseValidationProgressLabel = computed(() =>
    progressLabel(PlaybookEvaluationType.CLAUSE_VALIDATION)
  );
  const uncommonsProgressLabel = computed(() =>
    progressLabel(PlaybookEvaluationType.UNCOMMONS_CHECK)
  );

  const classificationFailures = computed(() => failures(PlaybookEvaluationType.CLASSIFICATION));
  const extractionFailures = computed(() => failures(PlaybookEvaluationType.EXTRACTION));
  const clauseValidationFailures = computed(() =>
    failures(PlaybookEvaluationType.CLAUSE_VALIDATION)
  );
  const uncommonsFailures = computed(() => failures(PlaybookEvaluationType.UNCOMMONS_CHECK));

  async function searchPlaybooks(text = ''): Promise<void> {
    playbooksLoading.value = true;
    playbooksError.value = null;
    try {
      const result = await PlaybooksService.search({
        text,
        pageSize: PLAYBOOK_PAGE_SIZE,
        columnFilters: { status: ['active'] },
      });
      const options: SelectOption[] = result.results.map(playbook => ({
        label: playbook.name,
        value: playbook.uid,
      }));
      const selected = selectedPlaybookOption.value;
      playbookOptions.value =
        selected && !options.some(option => option.value === selected.value)
          ? [selected, ...options]
          : options;
    } catch (error) {
      playbooksError.value = extractErrorDetail(error, 'Failed to load playbooks.');
    } finally {
      playbooksLoading.value = false;
    }
  }

  /** A run belongs to the playbook it was started for, so switching playbooks clears it. */
  async function selectPlaybook(uid: string | null): Promise<void> {
    if (uid === selectedPlaybookUid.value) {
      return;
    }

    selectedPlaybookUid.value = uid;
    selectedPlaybookOption.value =
      playbookOptions.value.find(option => option.value === uid) ?? null;
    reset();
    evaluations.value = [];

    if (uid) {
      await loadEvaluations(uid);
    }
  }

  async function loadEvaluations(playbookUid: string): Promise<void> {
    try {
      evaluations.value = await PlaybooksService.listEvaluations(playbookUid);
    } catch (error) {
      evaluations.value = [];
      playbooksError.value = extractErrorDetail(error, 'Failed to load playbook evaluations.');
    }
  }

  /**
   * A playbook's most recent runs, for the "load a previous run" cascader. Limited to runs
   * started from the add-in — those are the ones submitted with `raw_input`.
   */
  async function loadRuns(playbookUid: string): Promise<PlaybookRunSearchResult | null> {
    try {
      return await PlaybooksService.searchRuns(playbookUid, {
        pageSize: RUN_PAGE_SIZE,
        inputType: 'raw_input',
      });
    } catch (error) {
      runError.value = extractErrorDetail(error, 'Failed to load playbook runs.');
      return null;
    }
  }

  /**
   * Load an existing run's results, switching the selected playbook when needed.
   * `selectPlaybook` is a no-op when the playbook is already selected, so the
   * previous run's local decisions and error are cleared here either way.
   */
  async function selectRun(playbookUid: string, runUid: string): Promise<void> {
    await selectPlaybook(playbookUid);
    reset();
    await refreshRun(runUid);
  }

  async function startRun(): Promise<void> {
    const playbookUid = selectedPlaybookUid.value;
    if (!playbookUid || isStartingRun.value || isRunActive.value) {
      return;
    }

    isStartingRun.value = true;
    runError.value = null;
    resetResults();

    try {
      const documentBlocks = toPageBlocks(await readCurrentWordDocument());
      if (!documentBlocks.length) {
        runError.value = 'The current Word document does not contain readable text.';
        return;
      }

      const summary = await PlaybooksService.startRun(playbookUid, {
        raw_input: documentBlocks,
        // The add-in only ever renders suggestions, so nothing else is executed.
        skip_action_types: Object.values(PlaybookActionType).filter(
          actionType => actionType !== PlaybookActionType.SUGGESTION
        ),
      });
      // The create response carries no evaluation results — fetch the full run.
      await refreshRun(summary.uid);
    } catch (error) {
      runError.value = extractErrorDetail(error, 'Failed to start the playbook run.');
    } finally {
      isStartingRun.value = false;
    }
  }

  async function refreshRun(runUid: string): Promise<void> {
    try {
      run.value = await PlaybooksService.getRun(runUid);
    } catch (error) {
      runError.value = extractErrorDetail(error, 'Failed to load the playbook run.');
    }
  }

  async function applySuggestion(
    suggestion: WordSuggestion
  ): Promise<ApplySuggestionResult | null> {
    if (suggestion.status === 'declined' || applyingSuggestionId.value) {
      return null;
    }

    applyingSuggestionId.value = suggestion.id;
    try {
      const result = await applySuggestionToWord(suggestion);
      setSuggestionStatus(suggestion.id, 'accepted');
      setSuggestionFailed(suggestion.id, false);
      return result;
    } finally {
      applyingSuggestionId.value = null;
    }
  }

  function declineSuggestion(suggestionId: string): void {
    setSuggestionStatus(suggestionId, 'declined');
    setSuggestionFailed(suggestionId, false);
  }

  function setSuggestionFailed(suggestionId: string, failed: boolean): void {
    if (failed === failedSuggestionIds.value.has(suggestionId)) {
      return;
    }

    const next = new Set(failedSuggestionIds.value);
    if (failed) {
      next.add(suggestionId);
    } else {
      next.delete(suggestionId);
    }
    failedSuggestionIds.value = next;
  }

  async function focusSuggestion(suggestion: WordSuggestion): Promise<void> {
    if (focusingSuggestionId.value) {
      return;
    }

    focusingSuggestionId.value = suggestion.id;
    try {
      await focusSuggestionInWord(suggestion);
    } finally {
      focusingSuggestionId.value = null;
    }
  }

  async function focusFinding(finding: UncommonsCheckFinding): Promise<void> {
    if (focusingFindingId.value) {
      return;
    }

    focusingFindingId.value = finding.id;
    try {
      await focusSuggestionInWord(findingAsSuggestion(finding));
    } finally {
      focusingFindingId.value = null;
    }
  }

  function closeFinding(finding: UncommonsCheckFinding): void {
    closedFindingIds.value = new Set(closedFindingIds.value).add(finding.id);
  }

  function revertFinding(finding: UncommonsCheckFinding): void {
    const next = new Set(closedFindingIds.value);
    next.delete(finding.id);
    closedFindingIds.value = next;
  }

  function setSuggestionStatus(suggestionId: string, suggestionStatus: WordSuggestionStatus): void {
    suggestionStatuses.value = new Map(suggestionStatuses.value).set(
      suggestionId,
      suggestionStatus
    );
  }

  function resetResults(): void {
    run.value = null;
    suggestionStatuses.value = new Map();
    closedFindingIds.value = new Set();
    failedSuggestionIds.value = new Set();
    applyingSuggestionId.value = null;
    focusingSuggestionId.value = null;
    focusingFindingId.value = null;
  }

  function reset(): void {
    resetResults();
    runError.value = null;
  }

  return {
    playbookOptions,
    playbooksLoading,
    playbooksError,
    selectedPlaybookUid,
    run,
    isStartingRun,
    runError,
    runFailureMessage,
    isRunActive,
    suggestions,
    pendingSuggestions,
    extractions,
    clauseEntries,
    pendingClauseSuggestions,
    uncommonsFindings,
    classificationStatus,
    extractionStatus,
    clauseValidationStatus,
    uncommonsStatus,
    classificationProgressLabel,
    extractionProgressLabel,
    clauseValidationProgressLabel,
    uncommonsProgressLabel,
    classificationFailures,
    extractionFailures,
    clauseValidationFailures,
    uncommonsFailures,
    applyingSuggestionId,
    focusingSuggestionId,
    focusingFindingId,
    failedSuggestionIds,
    searchPlaybooks,
    selectPlaybook,
    loadRuns,
    selectRun,
    startRun,
    refreshRun,
    applySuggestion,
    declineSuggestion,
    setSuggestionFailed,
    focusSuggestion,
    focusFinding,
    closeFinding,
    revertFinding,
    reset,
  };
});

/** A finding as the suggestion shape the Word helpers take; anchored by its text. */
export function findingAsSuggestion(finding: UncommonsCheckFinding): WordSuggestion {
  return {
    id: finding.id,
    result_type: 'suggestion',
    title: finding.reason,
    explanation: finding.reason,
    status: 'pending',
    text_to_replace: finding.text,
  };
}
